import { api } from "roboflex-thalamus-request-handler";

function assistantData(response) {
    return response?.data?.data || response?.data || response
}

export default {
    async createAssistantSession(pageContext) {
        const response = await api.post('ai/assistant/sessions', {
            page_context: pageContext,
        })
        return assistantData(response)
    },

    async getAssistantSession(sessionId) {
        const response = await api.get(`ai/assistant/sessions/${sessionId}`)
        return assistantData(response)
    },

    async listAssistantSessions(params = {}) {
        const response = await api.get('ai/assistant/sessions', { params })
        return response?.data || response
    },

    async updateAssistantSession(sessionId, payload) {
        const response = await api.patch(`ai/assistant/sessions/${sessionId}`, payload)
        return assistantData(response)
    },

    async sendAssistantMessage(sessionId, message, pageContext, attachments = []) {
        let payload = { message, page_context: pageContext }
        if (attachments.length) {
            payload = new FormData()
            payload.append('message', message)
            payload.append('page_context', JSON.stringify(pageContext))
            attachments.forEach((file) => payload.append('attachments[]', file))
        }
        const response = await api.post(`ai/assistant/sessions/${sessionId}/messages`, payload)
        return assistantData(response)
    },

    async getAssistantInvocationProgress(sessionId) {
        return this.getAssistantSession(sessionId)
    },

    async confirmAssistantInvocation(sessionId, invocationId, payload = {}) {
        const response = await api.post(
            `ai/assistant/sessions/${sessionId}/invocations/${invocationId}/confirm`,
            payload,
        )
        return assistantData(response)
    },

    async cancelAssistantInvocation(sessionId, invocationId) {
        const response = await api.post(
            `ai/assistant/sessions/${sessionId}/invocations/${invocationId}/cancel`,
        )
        return assistantData(response)
    },

    async retryAssistantInvocation(sessionId, invocationId) {
        const response = await api.post(
            `ai/assistant/sessions/${sessionId}/invocations/${invocationId}/retry`,
        )
        return assistantData(response)
    },

    async listKnowledgeDocuments(filters = {}) {
        const params = {}
        if (filters.module) params.module = filters.module
        if (filters.status) params.status = filters.status
        if (filters.type) params.type = filters.type
        const response = await api.get('ai/governance/knowledge', { params })
        return response.data
    },

    async getKnowledgeDocument(id) {
        const response = await api.get(`ai/governance/knowledge/${id}`)
        return response.data
    },

    async uploadKnowledgeDocument(formData, { onUploadProgress } = {}) {
        const response = await api.post('ai/governance/knowledge', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress,
        })
        return response.data
    },

    async reindexKnowledgeDocument(id) {
        const response = await api.post(`ai/governance/knowledge/${id}/reindex`)
        return response.data
    },

    async updateKnowledgeDocumentStatus(id, status) {
        const response = await api.patch(`ai/governance/knowledge/${id}/status`, { status })
        return response.data
    },

    async deleteKnowledgeDocument(id) {
        const response = await api.delete(`ai/governance/knowledge/${id}`)
        return response.data
    },

    async searchKnowledge({ query, module = null, documentId = null, limit = 5 } = {}) {
        const payload = { query, limit }
        if (module) payload.module = module
        if (documentId) payload.document_id = documentId
        const response = await api.post('ai/governance/knowledge/search', payload)
        return response.data
    },

    async downloadKnowledgeFile(id) {
        const response = await api.get(`ai/governance/knowledge/${id}/file`, {
            responseType: 'blob',
        })
        return response
    },

    async listGovernanceUsers({ q = '', page = 1, perPage = 20 } = {}) {
        const response = await api.get('ai/governance/users', {
            params: { q, page, per_page: perPage },
        })
        return response.data
    },

    async saveGovernanceUserPolicy(userId, payload) {
        const response = await api.put(`ai/governance/users/${userId}/policy`, payload)
        return response.data
    },

    async getGovernanceUsage(filters = {}) {
        const response = await api.get('ai/governance/usage', { params: filters })
        return response.data
    },

    async getGovernanceInstructions() {
        const response = await api.get('ai/governance/instructions')
        return response.data
    },

    async saveGovernanceInstructions(items) {
        const response = await api.put('ai/governance/instructions', { items })
        return response.data
    },

    async getGovernanceMemory(filters = {}) {
        const response = await api.get('ai/governance/memory', { params: filters })
        return response.data
    },

    async updateGovernanceMemory(id, payload) {
        const response = await api.patch(`ai/governance/memory/${id}`, payload)
        return response.data
    },

    async deleteGovernanceMemory(id) {
        const response = await api.delete(`ai/governance/memory/${id}`)
        return response.data
    },

    async getGovernanceCapabilities() {
        const response = await api.get('ai/governance/capabilities')
        return response.data
    },
}
